import React, { useEffect, useState } from 'react';
import styles from './DatesList.module.css';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const pad = (n) => String(n).padStart(2, '0');

const formatDate = (date, dateFormat, includeDay = false) => {
  try {
    const year = date.getFullYear();
    const formatted = dateFormat
      .replace(/YYYY/g, year)
      .replace(/YY/g, String(year).slice(-2))
      .replace(/MM/g, pad(date.getMonth() + 1))
      .replace(/DD/g, pad(date.getDate()));
    return includeDay ? `${WEEKDAYS[date.getDay()]} ${formatted}` : formatted;
  } catch {
    return '';
  }
};

/**
 * @typedef {Object} DatesListProps
 * @property {(string|Date)[]} [dates] - Array of dates, grouped into consecutive ranges
 * @property {boolean} [white=true] - If true, uses the dark purple theme; if false, uses the outlined theme
 * @property {string} [customClass] - Optional additional CSS class applied to each date item
 * @property {string} [dateFormat='DD/MM/YY'] - Format string with tokens: YYYY, YY, MM, DD
 */

/**
 * Displays a list of dates grouped into consecutive ranges.
 *
 * @param {DatesListProps} props
 * @returns {React.ReactElement}
 */
const DatesList = ({
  dates = [],
  white = true,
  customClass = '',
  dateFormat = 'DD/MM/YY',
}) => {
  const [aggregatedDates, setAggregatedDates] = useState([]);

  useEffect(() => {
    if (!dates || dates.length === 0) {
      setAggregatedDates([]);
      return;
    }

    const sorted = dates.map((d) => new Date(d));
    sorted.sort((a, b) => a - b);

    const ranges = [[sorted[0]]];
    for (let i = 0; i < sorted.length - 1; i++) {
      const daysDiff = Math.round((sorted[i + 1] - sorted[i]) / (1000 * 60 * 60 * 24));
      if (daysDiff === 1) {
        ranges[ranges.length - 1].push(sorted[i + 1]);
      } else {
        ranges.push([sorted[i + 1]]);
      }
    }

    setAggregatedDates(ranges);
  }, [dates]);

  return (
    <div className={`${styles.container} dates-list-container`}>
      {aggregatedDates.map((range, index) => {
        const itemClasses = [
          styles.dateItem,
          'dates-list-item',
          white ? styles.purpleBg : styles.notWhite,
          customClass,
        ].filter(Boolean).join(' ');

        return (
          <div className={itemClasses} key={index}>
            {range.length === 1 ? (
              <span className={white ? styles.textStyle : ''}>
                {formatDate(range[0], dateFormat, true)}
              </span>
            ) : (
              <span className={white ? styles.textStyle : ''}>
                {formatDate(range[0], dateFormat)} - {formatDate(range[range.length - 1], dateFormat)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DatesList;
